import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { FocusHeader } from '../components/FocusHeader';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Wand2, Check } from 'lucide-react';

const MIN_BLOGS = 2;
const MAX_BLOGS = 6;

interface Blog {
  id: string;
  slug: string;
  title: string;
  intro: string;
  featured_image_url: string | null;
}

export function CreateCampaignMultiBlog() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [blogs, setBlogs] = useState<Blog[]>([]);
  // Volgorde van selecteren = volgorde van de teasers in de email.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (user) {
      loadBlogs();
    } else {
      navigate('/login');
    }
  }, [user, navigate]);

  const loadBlogs = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('published_blogs')
        .select('id, slug, title, intro, featured_image_url')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('published_at', { ascending: false });

      if (error) throw error;
      setBlogs(data || []);
    } catch (err) {
      console.error('Error loading blogs:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleBlog = (blogId: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(blogId)) {
        return prev.filter((id) => id !== blogId);
      }
      if (prev.length >= MAX_BLOGS) {
        return prev;
      }
      return [...prev, blogId];
    });
  };

  const handleContinue = () => {
    if (selectedIds.length < MIN_BLOGS) return;
    navigate('/campaigns/create/multi-blog/generate', {
      state: { blogIds: selectedIds },
    });
  };

  const selectionFull = selectedIds.length >= MAX_BLOGS;

  if (loading) {
    return (
      <>
        <FocusHeader
          title="Nieuwsbrief van meerdere blogs"
          onBack={() => navigate('/campaigns/create')}
        />
        <main className="flex-1 overflow-y-auto bg-[#F2F2EF] p-6">
          <LoadingSpinner text="Laden..." />
        </main>
      </>
    );
  }

  return (
    <>
      <FocusHeader
        title="Nieuwsbrief van meerdere blogs"
        subtitle="Selecteer de blogs die je in de nieuwsbrief wilt bundelen"
        onBack={() => navigate('/campaigns/create')}
      />
      <main className="flex-1 overflow-y-auto bg-[#F2F2EF] p-6">
        <div className="space-y-6 max-w-6xl mx-auto pb-28">
          <div className="card space-y-6">
            {blogs.length < MIN_BLOGS ? (
              <div className="text-center py-8">
                <p className="text-gray-600 mb-4">
                  {blogs.length === 0
                    ? 'Je hebt nog geen gepubliceerde blogs om een nieuwsbrief van te maken.'
                    : `Je hebt minimaal ${MIN_BLOGS} gepubliceerde blogs nodig voor een nieuwsbrief met meerdere blogs.`}
                </p>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="btn-primary"
                >
                  {blogs.length === 0 ? 'Maak je Eerste Blog' : 'Maak een Nieuwe Blog'}
                </button>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Selecteer Blogs
                  </label>
                  <span className="text-xs text-gray-500">
                    De volgorde van selecteren bepaalt de volgorde in de email
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {blogs.map((blog) => {
                    const orderIndex = selectedIds.indexOf(blog.id);
                    const isSelected = orderIndex !== -1;
                    const isDisabled = !isSelected && selectionFull;

                    return (
                      <button
                        key={blog.id}
                        onClick={() => toggleBlog(blog.id)}
                        aria-pressed={isSelected}
                        disabled={isDisabled}
                        className={`
                          w-full relative text-left rounded-lg border-2 transition-all overflow-hidden
                          ${isSelected
                            ? 'border-[#00B8A9] bg-[#00B8A9]/5 shadow-lg ring-2 ring-[#00B8A9]/20'
                            : isDisabled
                              ? 'border-gray-200 bg-white opacity-50 cursor-not-allowed'
                              : 'border-gray-200 bg-white hover:border-[#00B8A9]/50 hover:shadow-md'
                          }
                        `}
                      >
                        {blog.featured_image_url && (
                          <div className="w-full h-32 overflow-hidden">
                            <img
                              src={blog.featured_image_url}
                              alt={blog.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="p-4">
                          <h3 className="font-semibold text-[#231F20] mb-2 line-clamp-2">
                            {blog.title}
                          </h3>
                          <p className="text-sm text-gray-600 line-clamp-3">
                            {blog.intro}
                          </p>
                        </div>
                        {isSelected && (
                          <div className="absolute top-3 right-3 bg-[#00B8A9] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shadow-lg">
                            {orderIndex + 1}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {blogs.length >= MIN_BLOGS && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
            <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm">
                {selectedIds.length >= MIN_BLOGS ? (
                  <Check className="w-4 h-4 text-[#00B8A9]" />
                ) : null}
                <span className={selectedIds.length >= MIN_BLOGS ? 'text-[#231F20] font-medium' : 'text-gray-500'}>
                  {selectedIds.length} van maximaal {MAX_BLOGS} blogs geselecteerd
                </span>
                {selectedIds.length < MIN_BLOGS && (
                  <span className="text-gray-400">
                    (minimaal {MIN_BLOGS})
                  </span>
                )}
              </div>
              <button
                onClick={handleContinue}
                disabled={selectedIds.length < MIN_BLOGS}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Wand2 className="w-5 h-5" />
                Doorgaan met genereren
              </button>
            </div>
          </div>
        )}
      </main>
    </>
  );
}
